'use strict';

import { BufferGeometry, Group, Line, LineBasicMaterial, MathUtils, Matrix4, Vector3 } from 'three';
import { RoutsEnvironment } from './RoutsEnvironment';
import { BaseElement } from '../../../interfaces/BaseElement';
import { changeLangToCurrent, getCurrentLang } from '../../langSwitcher/langSwitcher';
import { CSS2DObject } from '../../../../libs/CSS2DRenderer';
import {lordIconColors, setLordIcon} from '../../../../lordicon';
import {
    addBuildingsIntersections,
    addPoiToIntersection,
    isBuildingIntersectionsSet,
    poiIntersection, removePoiFromIntersection,
} from '../../../poi/poiCss';
import { isElementHovered, labelVisibility } from '../../../../functions';
import { PublicLayers } from '../../publicLayers/PublicLayers';
import { GSAPAnimationMixer, isMobile, projectMode, threeJsVariables } from '../../../../settings';
import { OrbitDayAndNight } from '../../../liveAnimations/DayNight/OrbitDayAndNight';
import { getOrbitControlsDistanceAndPan, getPolarAngleFromViewMode, getWorldData, hideOrbitToMapBackButton, hideOrbitToMapButton, setMinAndMaxControlsDistance, setOrbitControls, setPanLimits, setPolarAngle, showOrbitToMapBackButton, showOrbitToMapButton } from '../../../controls/orbitControls';
import { animateCamera } from '../../../threejs/animation';
import { ViewMode } from '../../../../common';
import { Environment } from '../Environment';


export class EnvironmentLayer extends RoutsEnvironment {
    constructor (id) {
        super();

        this.id = id;
        this.active = false;
        this.objects = [];
        this.iconColors = {};
        this.group = new Group();
        this.group.visible = false;
        this.group.name = this.id;
        this.poiLineMaterial = new LineBasicMaterial({color: '#ffffff', transparent: true, depthWrite: !projectMode.AirMode.active,depthTest: !projectMode.AirMode.active});
        this.animateCamera = false;
    }

    show() {
        if (threeJsVariables.cssLabelManager.isBlocked(this.id))
            return;
        
        this.active = true;
        !isBuildingIntersectionsSet() && addBuildingsIntersections();
        this.create();
        if (!OrbitDayAndNight.isActiveAndSelected) {
            this.hideLabelsByDistance(true);
        }
        this.group.visible = true;
    }

    hide() {
        if (threeJsVariables.cssLabelManager.isBlocked(this.id))
            return;
        
        this.active = false;
        this.group.visible = false;
        this.clear();
    }

    create() {
        this.objects.forEach(obj => {
            this.createLabel(obj);
        });
    }

    loadFromBundle(scene,conf) {
        const config = conf[this.id];
        if (!config) return;
        this.settings = config;
    }

    clear() {
        this.objects.forEach(obj => {
            this.removeRoute(obj);

            if (obj.line) {
                obj.line.removeFromParent();
                obj.line.geometry.dispose();
                obj.line.material.dispose();
                delete obj.line;
            }

            if (obj.label) {
                removePoiFromIntersection(obj.label);
                obj.label.removeFromParent();
                delete obj.label;
            }
        })
    }

    createPublicButton(parent) {
        if (this.objects.length === 0) return;

        this.#getIconColors();
        this.publicLayerBtn = new BaseElement('button')
            .addClass('public-layer__btn',this.id)
            .addAttribute('data-public-layer',this.id)
            .event('mouseover', this.mouseOverHandler.bind(this))
            .event('mouseout', this.mouseOutHandler.bind(this))
            .appendTo(parent)
            .DOMElement;

        const iconParameters = {
            trigger: 'hover',
            stroke: 70
        }

        if (document.body.classList.contains('light-theme')) {
            iconParameters.id = 'public-layer__btn';
            iconParameters.primaryColor = '#fff';
            iconParameters.secondaryColor = '#fff';
        }

        setLordIcon(this.id.toLowerCase(), this.publicLayerBtn, iconParameters);

        new BaseElement('span')
            .addClass('public-layer__btn-text', 'language-string')
            .addAttribute('data-dictionary', this.id)
            .innerHtml(changeLangToCurrent(this.id))
            .appendTo(this.publicLayerBtn);
    }

    mouseOverHandler() {
        if (!this.active || (this.active && isElementHovered(this.publicLayerBtn))) {
            const icon = this.publicLayerBtn.querySelector('.lord__icon');
            icon.setAttribute('colors',`primary:${this.iconColors.activeColor},secondary:${this.iconColors.activeColor}`);
            this.publicLayerBtn.style.color = this.iconColors.activeColor;
        }
    }

    mouseOutHandler() {
        if (!this.active) {
            const icon = this.publicLayerBtn.querySelector('.lord__icon');
            const customColorId =  icon.getAttribute('custom-color-id');
            if (customColorId) {
                icon.setAttribute('colors',`primary:${lordIconColors.customColors[customColorId].primaryColor},secondary:${lordIconColors.customColors[customColorId].secondaryColor}`);
            } else {
                icon.setAttribute('colors',`primary:${this.iconColors.primaryColor},secondary:${this.iconColors.secondaryColor}`);
            }

            this.publicLayerBtn.style.color = '';
        }
    }

    publicButtonHandler() {
        if (this.group.visible) {
            this.hide();
            this.mouseOutHandler();
        } else {
            this.show();
            this.mouseOverHandler();
        }
    }

    createLabel(object) {
        if (!object.isLabel) return;

        const tags = object.tags;
        const names = {};

        config.languages.forEach(lang => {
            if (tags.hasOwnProperty(`name:${lang}`)) {
                names[lang] = tags[`name:${lang}`];
            } else if (tags.hasOwnProperty('name')) {
                names[lang] = config.dictionary[tags.name] ? config.dictionary[tags.name][lang] : tags.name;
            } else {
                const name = tags.leisure || tags.amenity || tags.building || tags.railway;
                const upperCase = name.charAt(0).toUpperCase() + name.slice(1);
                names[lang] = config.dictionary[upperCase] ? config.dictionary[upperCase][lang] : upperCase;
            }
        });

        const labelContainer = new BaseElement()
            .addClass('public-label')
            .DOMElement;

        new BaseElement()
            .addClass('public-label__type',tags.assets.icon)
            .addStyle('background-color',tags.assets.color)
            .appendTo(labelContainer);

        const textBlock = new BaseElement()
            .addClass('public-label__text-block')
            .appendTo(labelContainer)
            .DOMElement;

        const text = new BaseElement('span')
            .addClass('public-label__text','language-string')
            .innerHtml(names[getCurrentLang()])
            .appendTo(textBlock);

        Object.entries(names).forEach(([key,value]) => {
            text.addAttribute(`data-lang-${key}`,value);
        });

        new BaseElement('span')
            .addClass('public-label-text__separator')
            .innerHtml('|')
            .appendTo(textBlock);

        new BaseElement('span')
            .addClass('public-label__distance')
            .innerHtml(object.distance + 'm')
            .appendTo(textBlock);

        const label = new CSS2DObject(labelContainer);
        label.userData.zoom = 1;
        label.name = `layer-poi__${object.id}`;
        label.position.copy(object.point);
        label.element.onclick = () => {
            if (config.features.environment?.buildRoutsOnClick === false || isMobile)
                return;
            this.removePreviouslyRoute();
            this.buildRoute(object);
        };

        addPoiToIntersection(label);
        poiIntersection();

        const lineGeometry = new BufferGeometry().setFromPoints([
            object.point,
            new Vector3().copy(object.point).setY(object.altitude)
        ]);

        const line = new Line(lineGeometry,this.poiLineMaterial);
        line.name = 'poi_line';

        object.label = label;
        object.line = line;

        this.group.add(label,line);
    }
    
    hideLabelsByDistance(state) {
        const labels = this.objects.filter(el => el.isLabel && el.distance > 400);
        labels.forEach(data => labelVisibility(data,state));
    }

    getObjectById(id) {
        return this.objects.find(obj => obj.id === id);
    }

    #getIconColors() {
        // const { primaryColor, secondaryColor, activeColor } = getLordIconColor();

        this.iconColors.primaryColor = lordIconColors.primary;
        this.iconColors.secondaryColor = lordIconColors.secondary;
        this.iconColors.activeColor = lordIconColors.active;
    }

    static animateCameraShow() {
        const {cameraVector, orbitControlsVector} = getWorldData();
        const target = config.threejs.orbitControls.target;
        const minAngle = MathUtils.degToRad(10);
        const maxAngle = MathUtils.degToRad(70);
        const targetAngle = (minAngle + maxAngle) / 2;
        const minDistance = getOrbitControlsDistanceAndPan(ViewMode.Orbit).minDistance;
        const maxDistance = 460;
        const length = maxDistance;

        const defaultTarget = new Vector3(target.x, target.y, target.z);
        const position = new Vector3()
            .subVectors(cameraVector, orbitControlsVector).setY(0).normalize()
            .setY(1 / Math.tan(targetAngle))
            .setLength(length).add(orbitControlsVector);

        const mixer = GSAPAnimationMixer.registerNewMixer('showEnvironment');
        const cameraAnimation = animateCamera(
            cameraVector,
            position,
            orbitControlsVector,
            true ? orbitControlsVector : defaultTarget,  // test
            undefined,
            length,
            targetAngle,
            0,
            1500,
            TWEEN.Easing.Sinusoidal.InOut,
            () => {
                threeJsVariables.trackballControls.maxDistance = maxDistance;
                setOrbitControls(ViewMode.Orbit);
                showOrbitToMapBackButton();
            },
            () => {
                threeJsVariables.trackballControls.minDistance = minDistance;
                threeJsVariables.trackballControls.maxDistance = maxDistance;
                threeJsVariables.orbitControls.minPolarAngle = minAngle;
                threeJsVariables.orbitControls.maxPolarAngle = maxAngle;
                setPanLimits(ViewMode.Orbit);
                showOrbitToMapButton();
            },
        );
        mixer.addAnimation(cameraAnimation);
        mixer.play();
    }

    static animateCameraHide() {
        const {cameraVector, orbitControlsVector} = getWorldData();
        const target = config.threejs.orbitControls.target;
        const {minAngle} = getPolarAngleFromViewMode(ViewMode.Orbit);
        const {minDistance, maxDistance} = getOrbitControlsDistanceAndPan(ViewMode.Orbit);
        const foreshortening = config.threejs.orbitControls.autoZoomSettings.foreshortening;
        const length = (maxDistance + minDistance) / 2;

        const defaultTarget = new Vector3(target.x, target.y, target.z);
        const position = new Vector3().subVectors(cameraVector, orbitControlsVector).normalize().setY(0).setLength(length).add(orbitControlsVector);
        const mixer = GSAPAnimationMixer.registerNewMixer('hideEnvironment');
        const cameraAnimation = animateCamera(
            cameraVector,
            config.features.privateHouseMode ?
                cameraVector : position,
            orbitControlsVector,
            config.features.privateHouseMode ? orbitControlsVector : defaultTarget,
            undefined,
            config.features.privateHouseMode ? this.minDistance : length,
            minAngle,
            0,
            1500,
            TWEEN.Easing.Sinusoidal.InOut,
            () => {
                threeJsVariables.trackballControls.maxDistance = maxDistance;
                setOrbitControls(ViewMode.Orbit);
                setPolarAngle(ViewMode.Orbit);
                setPanLimits(ViewMode.Orbit);
                hideOrbitToMapButton();
                hideOrbitToMapBackButton();
            },
            () => {
                setMinAndMaxControlsDistance(ViewMode.Orbit);
            },
        );
        mixer.addAnimation(cameraAnimation);
        mixer.play();
    }

    set settings(data) {
        this.group.position.fromArray(data.position);
        data.objects.forEach(obj => {
            if (obj.point) {
                // if (projectMode.AirMode.active) {
                //     obj.point = projectMode.AirMode.project(obj);
                //     const matrix = new Matrix4().fromArray(Environment.scope.config.Environment.matrix).invert();
                //     obj.environmentMatrixInverse = matrix;
                //     obj.point.applyMatrix4(matrix);
                // } else {
                    obj.point = new Vector3().fromArray(obj.point);
                // }
            }

            const parsedRouteVectors = [];
            obj.route.forEach(point => {
                parsedRouteVectors.push(new Vector3().fromArray(point));
            });

            obj.route = parsedRouteVectors;
        });
        this.objects = data.objects;
    }

    static resetToDefault() {
        const publicLayers = document.querySelector('.public-layers__elements');
        if (!publicLayers)
            return;

        // const {primaryColor, secondaryColor} = getLordIconColor();
        const allButtons = publicLayers.querySelectorAll('.public-layer__btn');
        allButtons.forEach(button => {
            const icon = button.querySelector('.lord__icon');
            const customColorId = icon.getAttribute('custom-color-id');
            if (customColorId) {
                icon.setAttribute('colors',`primary:${lordIconColors.customColors[customColorId].primaryColor},secondary:${lordIconColors.customColors[customColorId].secondaryColor}`);
            } else {
                icon.setAttribute('colors',`primary:${lordIconColors.primary},secondary:${lordIconColors.secondary}`);
            }
            // icon.setAttribute('colors',`primary:${lordIconColors.primary},secondary:${lordIconColors.secondary}`);
            button.style.color = '';
        });

        if (PublicLayers.isActive && PublicLayers.activeLayer && PublicLayers.activeLayer.type === 'poi')
            return;

        const publicButton = publicLayers.querySelector('[data-public-layer="poi"]');
        if (!publicButton)
            return;

        publicButton.click();
    }
}
